import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import MachineryPicker from './MachineryPicker';

const AddInventoryDialog = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    part_number: '',
    name: '',
    description: '',
    quantity: '',
    unit: 'pcs',
    location: '',
    reorder_level: '',
    unit_cost: '',
    supplier: '',
    notes: '',
    non_critical: false
  });
  const [applicableIds, setApplicableIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        quantity: parseInt(formData.quantity),
        reorder_level: formData.reorder_level ? parseInt(formData.reorder_level) : null,
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
        applicable_machinery_ids: applicableIds,
      };

      await axios.post(`${API}/inventory`, submitData);
      toast.success('Inventory item added successfully!');
      onSuccess();
      setFormData({
        part_number: '',
        name: '',
        description: '',
        quantity: '',
        unit: 'pcs',
        location: '',
        reorder_level: '',
        unit_cost: '',
        supplier: '',
        notes: '',
        non_critical: false
      });
      setApplicableIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add inventory item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Inventory Item</DialogTitle>
          <DialogDescription>Add a new part to inventory</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="part_number">Part Number *</Label>
              <Input
                id="part_number"
                data-testid="inventory-part-number-input"
                value={formData.part_number}
                onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                placeholder="e.g., 12345"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                data-testid="inventory-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Pressure Relief Valve"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              data-testid="inventory-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity *</Label>
              <Input
                id="quantity"
                data-testid="inventory-quantity-input"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit">Unit</Label>
              <select
                id="unit"
                data-testid="inventory-unit-select"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="pcs">Pieces</option>
                <option value="kg">Kilograms</option>
                <option value="liter">Liters</option>
                <option value="meter">Meters</option>
                <option value="box">Boxes</option>
                <option value="set">Sets</option>
                <option value="5-Gal Pail" data-testid="inventory-unit-five-gal-pail">5-Gal Pail</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reorder_level">Reorder Level</Label>
              <Input
                id="reorder_level"
                data-testid="inventory-reorder-input"
                type="number"
                value={formData.reorder_level}
                onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })}
                placeholder="Min stock"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                data-testid="inventory-location-input"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Shelf A3, Warehouse 2"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit_cost">Unit Cost ($)</Label>
              <Input
                id="unit_cost"
                data-testid="inventory-cost-input"
                type="number"
                step="0.01"
                value={formData.unit_cost}
                onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="supplier">Supplier</Label>
            <Input
              id="supplier"
              data-testid="inventory-supplier-input"
              value={formData.supplier}
              onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              placeholder="Supplier name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              data-testid="inventory-notes-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Applies to equipment (optional)</Label>
            <MachineryPicker
              value={applicableIds}
              onChange={setApplicableIds}
              disabled={loading}
            />
          </div>

          <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <input
              id="non_critical"
              data-testid="inventory-non-critical-checkbox"
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-slate-300"
              checked={formData.non_critical}
              onChange={(e) => setFormData({ ...formData, non_critical: e.target.checked })}
            />
            <div>
              <Label htmlFor="non_critical" className="cursor-pointer font-medium">
                Non-critical (hide from low-stock alerts)
              </Label>
              <p className="text-xs text-slate-600 mt-0.5">
                Use for supplier-managed parts, heavy rebuild parts held at 0 qty, or parts so easily available they do not need stocking. Non-critical items are still tracked but will not clutter the low-stock alerts on the dashboard or inventory page.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-inventory-btn">
              {loading ? 'Adding...' : 'Add Item'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddInventoryDialog;
